import React from 'react';
import DayPicker from 'react-day-picker';
import {connect} from 'react-redux';
import keyBy from 'lodash/keyBy';
import get from 'lodash/get';
import eachDay from 'date-fns/each_day';
import getMonth from 'date-fns/get_month';
import getDate from 'date-fns/get_date';
import cs from 'classnames';

import DropDown from '~/components/common/DropDown';
import LabeledInput from '~/components/common/LabeledInput';
import keys from '~/misc/keys';
import {datesWithoutPast, isPastDay, formatDate, dayKey} from '~/misc/dates';
import {openDropdown, closeDropdown} from '~/modules/dropdowns/actions';

import css from './index.css';
import './react-day-picker.css';

const getValue = (props) => {
  const value = props.value || props.initialValue;
  // require withoutAny and value array is empty( which means select no days ) 
  // return now date.
  const array = Array.isArray(value) ? ((props.withoutAny&&!value.length) ? [props.nowDate] : value) : (value && [value]);
  return datesWithoutPast(array, props.nowDate);
};

const renderValue = (value) => {
  if (value.length === 0) {
    return 'Any date';
  }

  if (value.length === 1) {
    return formatDate(value[0], true);
  }

  // expected that value is array which is range

  const rangeStart = value[0];
  const rangeEnd = value[value.length - 1];

  if (getMonth(rangeStart) !== getMonth(rangeEnd)) {
    return `${formatDate(rangeStart)} - ${formatDate(rangeEnd)}`;
  }

  if (value.length > 2) {
    return `${formatDate(rangeStart)} - ${getDate(rangeEnd)}`;
  }


  return value
    .sort((a, b) => a - b)
    .map(formatDate)
    .map((i, idx) => ((idx === 0) ? i.match(/.[^,]+/)[0] : i.match(/\d+/)[0]))
    .join(', ');
};

const dontMove = keyBy([keys.TOP, keys.RIGHT, keys.BOTTOM, keys.LEFT, keys.SPACE]);

class CalendarInput extends React.Component {
  constructor(props) {
    super(props);
    this.handleAny = this.handleAny.bind(this);
    this.onInputKeyUp = this.onInputKeyUp.bind(this);
    this.onDropdownKeyPress = this.onDropdownKeyPress.bind(this);
    this.handleDayClick = this.handleDayClick.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.dropdownOpened !== this.props.dropdownOpened && !nextProps.dropdownOpened) {
      this.setState({canSelectRange: false});
    }
  }

  render() {
    return (
      <div className={cs(this.props.wrapClassName, `qa-${this.props['data-qa']}`)}>
        <DropDown
          data-qa={'dropdown'}
          id={this.props.dropDownId}
          wrapClassName={css.wrapDropdown}
          allowKeyboard={true}
          trigger={
            <LabeledInput
              {...this.props.input}
              data-qa={'input'}
              label={this.props.label}
              value={renderValue(getValue(this.props))}
              icon='calendar'
              readOnly={true}
              mediumInput
              onKeyUp={this.onInputKeyUp}
              onKeyDown={this.onKeyDown}
            />
          }
          content={this.renderSelect()}
          onKeyPress={this.onDropdownKeyPress}
        />
      </div>
    );
  }

  renderSelect() {
    const seletedDatesKeys = keyBy(getValue(this.props), dayKey);

    return (
      <div className={cs(css.dropdown, 'qa-calendar')}>
        <DayPicker
          disabledDays={(day) => this.disabledDays(day)}
          selectedDays={day => Boolean(seletedDatesKeys[dayKey(day)])}
          onDayClick={this.handleDayClick}
        />
      </div>
    );
  }

  disabledDays(day){
    if(this.props.disabledDays && typeof this.props.disabledDays === "function"){
      return this.props.disabledDays(day)
    }else{
      return isPastDay(day, this.props.nowDate)
    }
  }

  onDropdownKeyPress(keyCode, e) {
    if (keyCode !== keys.BKSP) {
      return;
    }

    this.removeLast();

    e.stopPropagation();
    e.preventDefault();
  }

  onKeyDown(e) {
    const keyCode = e.keyCode;

    if (!dontMove[keyCode]) {
      return;
    }

    e.stopPropagation();
    e.preventDefault();
  }

  onInputKeyUp(e) {
    const keyCode = e.keyCode;

    if (keyCode === keys.ENTER || keyCode === keys.TAB || keyCode === keys.ESC) {
      return null;
    }

    if (keyCode === keys.BKSP) {
      this.removeLast();
    } else {
      this.props.dispatch(openDropdown(this.props.dropDownId));
    }


    e.stopPropagation();
    e.preventDefault();

    return null;
  }

  removeLast() {
    const value = getValue(this.props);
    if (value.length) {
      this.props.onChange(value.slice(0, -1));
    }
  }

  handleAny() {
    this.props.onChange([]);
  }

  handleDayClick(e, day) {
    if (this.disabledDays(day)) {
      return null;
    }

    if (this.props.multiSelect) {
      return this.selectDays(day);
    }

    return this.selectDay(day);
  }

  selectDays(day) {
    if (isPastDay(day, this.props.nowDate)) {
      return null;
    }
    const value = getValue(this.props);

    if (!value || !value.length) {
      this.setState({canSelectRange: true});
      return this.props.onChange([day]);
    }

    const seletedDatesKeys = keyBy(value, dayKey);
    if (value.length === 1 && seletedDatesKeys[dayKey(day)]) {
      this.setState({canSelectRange: false});
      return this.props.onChange([]);
    }

    if (value.length > 1 || !get(this, 'state.canSelectRange')) {
      this.setState({canSelectRange: true});
      return this.props.onChange([day]);
    }

    const start = Math.min(value[0], day);
    const end = Math.max(value[0], day);

    this.setState({canSelectRange: false});
    return this.props.onChange(eachDay(start, end));
  }

  selectDay(day) {
    if (isPastDay(day, this.props.nowDate)) {
      return;
    }

    const value = getValue(this.props);

    if (value && dayKey(value[0]) === dayKey(day)) {
      this.props.onChange(null);
    } else {
      this.props.onChange(day);
    }

    this.props.dispatch(closeDropdown(this.props.dropDownId));
  }
}

export default connect((state, props) => ({
  dropdownOpened: state.dropdowns[props.dropDownId],
  nowDate: state.now.date,
}))(CalendarInput);



// WEBPACK FOOTER //
// ./src/components/common/CalendarInput/index.js